// AN INVOICE ALREADY IN POINT CLICK CARE BEFORE THIS APP (owner, 2026-10-20 —
// D-387). Each company's recent Point Click Care invoices are loaded once, at
// its launch, into `pcc_invoice_history`, so the exact duplicate check can
// catch a document that was entered there before the app existed. Nothing
// shows the table; a match surfaces only as an `Exact Duplicate Detected`
// flag.
//
// THE FLAG'S DETAIL CARRIES THE RECORD ITSELF. An in-app match's detail is
// the prior invoice's id, which Compare Invoices reads through
// `GET /invoices/{id}`. A history row has no invoice, no document and no
// route, so its detail is `pcc:` followed by the row as JSON, and Compare
// Invoices shows that. One function writes it and one reads it, so the two
// sides of the wire cannot disagree.

/** One prior Point Click Care invoice, as the flag carries it. */
export interface PccInvoiceHistoryMatch {
  company_id: string
  company_name: string | null
  vendor_id: string
  vendor_name: string | null
  /** The vendor's own invoice number, as Point Click Care holds it. */
  external_invoice_id: string
  /** `YYYY-MM-DD`: the earliest date the report shows for the number. */
  invoice_date: string | null
  /** A decimal string: the sum of the report's lines for the number. */
  amount: string | null
}

const PREFIX = 'pcc:'

/**
 * An invoice number as the history lookup compares it: letters and digits
 * only, upper case (owner, 2026-10-20). The history's number was typed by a
 * person and ours was read off the document, so punctuation, spaces and case
 * are noise; `9-489-71677` and `948971677` are one invoice. Leading zeros are
 * kept — they are digits. `pcc_invoice_history.invoice_key` is the same rule in
 * SQL, and the two must agree character for character.
 */
export function invoiceMatchKey(value: string): string {
  return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
}

/** The `DuplicateDeterministic` detail for a history match. */
export function pccHistoryDetail(match: PccInvoiceHistoryMatch): string {
  return PREFIX + JSON.stringify(match)
}

/** The match a flag detail carries, or null when the detail is not one. */
export function parsePccHistoryDetail(detail: string | null | undefined): PccInvoiceHistoryMatch | null {
  if (!detail || !detail.startsWith(PREFIX)) return null
  let value: unknown
  try {
    value = JSON.parse(detail.slice(PREFIX.length))
  } catch {
    return null
  }
  if (typeof value !== 'object' || value === null) return null
  const v = value as Record<string, unknown>
  const text = (key: string): string | null => (typeof v[key] === 'string' ? (v[key] as string) : null)
  const vendorId = text('vendor_id')
  const external = text('external_invoice_id')
  const companyId = text('company_id')
  if (vendorId === null || external === null || companyId === null) return null
  return {
    company_id: companyId,
    company_name: text('company_name'),
    vendor_id: vendorId,
    vendor_name: text('vendor_name'),
    external_invoice_id: external,
    invoice_date: text('invoice_date'),
    amount: text('amount'),
  }
}
